import { Entity } from '@backstage/catalog-model';
import { githubAuthApiRef, useApi } from '@backstage/core-plugin-api';
import { useGithubProjectEntity, useGithubUrl } from '@mb.io/core-components';
import { graphql } from '@octokit/graphql';
import useAsync from 'react-use/esm/useAsync';
import { DependabotNode, Repository } from '../types';

// GitHub caps a single vulnerabilityAlerts page at 100 nodes; page through all of them.
const MAX_PAGES = 20;

type AlertsPage = {
  totalCount: number;
  hasNextPage: boolean;
  endCursor: string | null;
  nodes: DependabotNode[];
};

const fetchAlertsPage = async (
  gqlEndpoint: ReturnType<typeof graphql.defaults>,
  query: string,
  variables: { name: string; owner: string; after: string | null },
): Promise<AlertsPage> => {
  const { repository } = await gqlEndpoint<{ repository: Repository }>(
    query,
    variables,
  );
  const alerts = repository.vulnerabilityAlerts;
  return {
    totalCount: alerts.totalCount,
    hasNextPage: alerts.pageInfo.hasNextPage,
    endCursor: alerts.pageInfo.endCursor,
    nodes: alerts.nodes,
  };
};

export const useDependabotAlerts = (entity: Entity) => {
  const { owner, repo } = useGithubProjectEntity(entity);
  const auth = useApi(githubAuthApiRef);
  const { hostname, graphqlBaseUrl } = useGithubUrl(entity);

  const query = `query GetDependabotAlertsWidget($name: String!, $owner: String!, $after: String) {
    repository(name: $name, owner: $owner) {
      vulnerabilityAlerts(first: 100, after: $after) {
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
        nodes {
          createdAt
          id
          dismissedAt
          vulnerableManifestPath
          state
          securityVulnerability {
            vulnerableVersionRange
            package {
              name
            }
            firstPatchedVersion {
              identifier
            }
            severity
            advisory {
              description
            }
          }
        }
      }
    }
  }`;

  const { value, loading, error } = useAsync(async (): Promise<any> => {
    const token = await auth.getAccessToken(['repo']);
    // @octokit/graphql appends "/graphql" itself; strip it here in case it's already present in config.
    const sanitizedGraphqlBaseUrl = graphqlBaseUrl.replace(/\/graphql\/?$/, '');
    const gqlEndpoint = graphql.defaults({
      baseUrl: sanitizedGraphqlBaseUrl,
      headers: {
        authorization: `token ${token}`,
      },
    });

    const nodes: DependabotNode[] = [];
    let totalCount = 0;
    let after: string | null = null;
    let hasNextPage = true;
    let page = 0;

    while (hasNextPage && page < MAX_PAGES) {
      const alertsPage = await fetchAlertsPage(gqlEndpoint, query, {
        name: repo,
        owner: owner,
        after,
      });
      totalCount = alertsPage.totalCount;
      nodes.push(...alertsPage.nodes);
      hasNextPage = alertsPage.hasNextPage;
      after = alertsPage.endCursor;
      page += 1;
    }

    return { vulnerabilityAlerts: { totalCount, nodes } };
  }, []);

  const repoLocation = { hostname, owner, repo };
  return {
    value,
    loading,
    error,
    repoLocation,
  };
};
